import React, { useState } from "react";
import {
  Users,
  Building2,
  Database,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { audiences, stats } from "../mock";

const tabs = [
  { id: "audiences", label: "Beacons", sub: "Build & Rank", icon: Users },
  { id: "visitors", label: "Visitors", sub: "Identify Anonymous", icon: Building2 },
  { id: "data", label: "Activate", sub: "Push to Channels", icon: Database },
];

export default function DashboardMock() {
  const [tab, setTab] = useState("audiences");

  return (
    <section className="relative pb-20 lg:pb-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        {/* segmented tabs */}
        <div className="flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
            {tabs.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`group flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-all ${
                    active
                      ? "bg-[#1e1b4b] text-white shadow-[0_6px_16px_-6px_rgba(12,27,45,0.5)]"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-medium">{t.label}</span>
                  <span
                    className={`hidden text-xs sm:inline ${
                      active ? "text-white/70" : "text-slate-400"
                    }`}
                  >
                    {t.sub}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* dashboard card */}
        <div className="relative mx-auto mt-10 max-w-6xl">
          <div className="absolute -inset-x-8 -top-6 bottom-0 -z-10 rounded-[32px] bg-gradient-to-b from-[#f59e0b]/10 via-transparent to-transparent blur-2xl" />
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_30px_80px_-30px_rgba(12,27,45,0.25)]">
            {/* browser chrome */}
            <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/70 px-4 py-3">
              <div className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
                <span className="h-2.5 w-2.5 rounded-full bg-slate-300" />
              </div>
              <div className="rounded-md border border-slate-200 bg-white px-3 py-1 text-xs text-slate-500">
                app.buyerbeacon.pro/beacons
              </div>
              <div className="w-12" />
            </div>

            <div className="grid grid-cols-12">
              {/* sidebar */}
              <aside className="col-span-12 border-b border-slate-200 bg-white p-4 sm:col-span-3 sm:border-b-0 sm:border-r">
                <div className="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                  <div className="flex h-6 w-6 items-center justify-center rounded-md bg-[#1e1b4b] text-[10px] font-semibold text-white">
                    BB
                  </div>
                  <span className="text-sm font-medium text-[#1e1b4b]">BuyerBeacon</span>
                </div>
                <nav className="mt-4 flex flex-col gap-0.5 text-sm">
                  <a className="flex items-center gap-2 rounded-md bg-[#1e1b4b]/[0.06] px-3 py-2 font-medium text-[#1e1b4b]">
                    <Users className="h-3.5 w-3.5" /> Beacons
                  </a>
                  <a className="flex items-center gap-2 rounded-md px-3 py-2 text-slate-600 hover:bg-slate-50">
                    <Building2 className="h-3.5 w-3.5" /> Visitors
                  </a>
                  <a className="flex items-center gap-2 rounded-md px-3 py-2 text-slate-600 hover:bg-slate-50">
                    <Database className="h-3.5 w-3.5" /> Activate
                  </a>
                  <a className="flex items-center gap-2 rounded-md px-3 py-2 text-slate-600 hover:bg-slate-50">
                    <Sparkles className="h-3.5 w-3.5" /> Pulse
                  </a>
                </nav>
                <div className="mt-6 hidden items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 sm:flex">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#f59e0b]/20 text-[10px] font-semibold text-[#7a4f00]">
                    JD
                  </div>
                  <span className="truncate text-xs text-slate-600">john@company.com</span>
                </div>
              </aside>

              {/* main */}
              <div className="col-span-12 p-5 sm:col-span-9 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-[#1e1b4b]">
                      Active Audiences
                    </h3>
                    <p className="text-xs text-slate-500">5 lists · refreshed 2 min ago</p>
                  </div>
                  <button className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50">
                    + New Audience
                  </button>
                </div>

                <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
                  <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-50/60 px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                    <div className="col-span-5">Audience</div>
                    <div className="col-span-3 hidden sm:block">Filters</div>
                    <div className="col-span-2">Beacon Score</div>
                    <div className="col-span-2 text-right">Records</div>
                  </div>
                  {audiences.map((a, i) => {
                    const score = a.beaconScore;
                    const tier = score >= 85 ? "Hot" : score >= 75 ? "Warm" : "Cool";
                    return (
                      <div
                        key={a.name}
                        className={`grid grid-cols-12 items-center gap-2 px-4 py-3 text-sm ${
                          i !== audiences.length - 1 ? "border-b border-slate-100" : ""
                        } hover:bg-slate-50/50`}
                      >
                        <div className="col-span-5 truncate font-medium text-[#1e1b4b]">
                          {a.name}
                        </div>
                        <div className="col-span-3 hidden flex-wrap gap-1 sm:flex">
                          {a.filters.slice(0, 2).map((f, idx) => (
                            <span
                              key={idx}
                              className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600"
                            >
                              {f.k}{f.v ? `: ${f.v}` : ""}
                            </span>
                          ))}
                        </div>
                        <div className="col-span-2 flex items-center gap-2">
                          <div className="relative h-1.5 w-12 overflow-hidden rounded-full bg-slate-200">
                            <div
                              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#d4af37] to-[#e9c659]"
                              style={{ width: `${score}%` }}
                            />
                          </div>
                          <span
                            className="font-mono text-xs font-semibold"
                            style={{ color: score >= 85 ? "#7c5e0a" : "#475569" }}
                          >
                            {score}
                          </span>
                          <span className="hidden text-[10px] uppercase tracking-wider text-slate-400 lg:inline">
                            {tier}
                          </span>
                        </div>
                        <div className="col-span-2 text-right font-mono text-sm text-[#1e1b4b]">
                          {a.records}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-center">
            <button className="group inline-flex items-center gap-1 text-xs font-medium uppercase tracking-[0.1em] text-slate-500 hover:text-[#1e1b4b]">
              Explore the platform
              <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </button>
          </div>
        </div>

        {/* stats strip */}
        <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="group rounded-2xl border border-slate-200 bg-white p-5 transition-all hover:border-[#1e1b4b]/20 hover:shadow-md"
            >
              <div className="text-3xl font-medium tracking-tight text-[#1e1b4b]">
                {s.value}
              </div>
              <div className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
